import { previewDestinations } from '../destinations';
import type { ReactNode } from 'react';
import logo240 from '../assets/logo-240.png';
import type { Copy } from '../../core/copy';

/**
 * The message. Three blocks and nothing else: the problem, the answer, the ask.
 *
 * What is deliberately absent is the point. No feature list, no roadmap, no dates, no
 * signup count. docs/teacher-lab.md is explicit that anything reading as a roadmap creates
 * an obligation this project cannot honour while the tool is still interchangeable, and
 * that a visible tally turns the page into the vote board it is avoiding.
 *
 * The words themselves live in core/copy.ts, one object per experiment arm.
 *
 * A signed-in 240 account sees the private tool directory instead of the marketing copy
 * (`showTools`). That view is not under the copy experiment.
 */

/**
 * Break a headline at its sentence boundaries.
 *
 * 240's headlines are two beats — "Study Smarter. Pass Confidently." — and they only
 * read that way if each beat gets its own line. Left to `text-wrap: balance`, a
 * two-sentence headline breaks mid-phrase, which reads as a mistake.
 *
 * Done by splitting rather than a hardcoded <br> because the copy is supplied by a
 * retired copy experiment, so no wording is assumed here. A single-sentence
 * headline comes back unchanged.
 */
export function toBeats(headline: string): string[] {
  const beats = headline.match(/[^.!?]+[.!?]*\s*/g);
  if (!beats || beats.length < 2) return [headline];
  return beats.map((b) => b.trim()).filter(Boolean);
}

export interface HeroProps {
  copy: Copy;
  accountAction?: ReactNode;
  showTools?: boolean;
  objectivesVisible?: boolean;
  onToolOpen?: () => void;
}

export default function Hero({ copy, accountAction, showTools = false, objectivesVisible = false, onToolOpen }: HeroProps) {
  const beats = toBeats(copy.headline);

  return (
    <>
      <div className="lockup">
        {/* Width and height reserve the row before the image decodes. */}
        <img
          className="lockup-logo"
          src={logo240}
          width={212}
          height={160}
          alt="240"
          decoding="async"
        />
        <span className="lockup-rule" aria-hidden="true" />
        <p className="eyebrow">A 240 project</p>
        {accountAction && <div className="account-action">{accountAction}</div>}
      </div>

      {showTools ? (
        <section aria-labelledby="tools-heading">
          <h1 className="headline" id="tools-heading">Your tools</h1>
          <p className="lede">Choose a 240 Labs tool.</p>
          {objectivesVisible ? <ul className="tool-list">
            <li>
              <a
                className="tool-card"
                href={previewDestinations(window.location.hostname).objectives}
                onClick={onToolOpen}
              >
                <strong>Standard Checker</strong>
                <span>Check material you found against academic standards.</span>
              </a>
            </li>
          </ul> : <p className="lede">No tools are available right now.</p>}
        </section>
      ) : (
        <>
          <h1 className="headline">
            {beats.map((beat, i) => (
              <span className="beat" key={beat}>
                {beat}
                {/* Keeps a word boundary in the heading's accessible name. The spans are
                    block-level, so this is invisible but stops a screen reader reading
                    "teachers.We". */}
                {i < beats.length - 1 ? ' ' : null}
              </span>
            ))}
          </h1>

          {copy.paragraphs.map((paragraph) => (
            <p className="lede" key={paragraph.slice(0, 40)}>
              {paragraph}
            </p>
          ))}

          <p className="lede cta-line">
            {/* No arrow here. Mid-sentence it has nothing to point at and reads as a broken
                bullet; it lives on the button instead, where forward motion means something. */}
            <strong className="cta-lead">{copy.ctaLead}.</strong> {copy.ctaBody}
          </p>
        </>
      )}
    </>
  );
}
